import { SectionLabel } from '../components/SectionLabel';
import { useCTAHandlers } from '~/ui-rocco/hooks/use-home-cta-actions';
import { ReactComponent as Arrow } from '../images/arrow.svg';

/**
 * Intro / mission statement (Figma `997:34953`, 1920 × 520).
 *
 * A two-tone brand statement — the first sentence in ink, the rest in grey —
 * prefixed by an "(About)" eyebrow that nests into the first line's indent,
 * a leading arrow, and a dark Get Started pill below.
 *
 * Frame-based layout (`aspect-[1920/520]` + absolute children in % / vw) so it
 * scales proportionally and the line breaks stay fixed, matching the B2B
 * landing sections. Mobile relaxes to a flowed stack.
 */
const FRAME_HEIGHT = 520;
const pctX = (x: number) => (x / 1920) * 100;
const pctY = (y: number) => (y / FRAME_HEIGHT) * 100;

const STATEMENT_LEAD = 'Creating a home is one of life’s biggest investments. ';
const STATEMENT_TAIL =
    'ArchiPro helps you find the inspiration, products and professionals to do it with confidence.';

export const IntroSection = () => {
    const { handleCTAClick, handleLoginClick } =
        useCTAHandlers('HomePageIntro');

    const onGetStarted = () => {
        handleCTAClick('startProject');
        handleLoginClick();
    };

    return (
        <section
            className="relative w-full bg-white text-[#181818]"
            style={{ aspectRatio: `1920 / ${FRAME_HEIGHT}` }}
        >
            {/* Desktop — absolute-positioned to the 1920 frame */}
            <div className="absolute inset-0 max-md:hidden">
                {/* Leading arrow */}
                <Arrow
                    className="absolute"
                    style={{
                        left: `${pctX(25)}%`,
                        top: `${pctY(10)}%`,
                        width: `${pctX(57.84)}%`,
                        height: `${pctY(42.24)}%`,
                    }}
                    aria-hidden="true"
                />

                {/* (About) eyebrow — sits in the first-line indent */}
                <span
                    className="absolute"
                    style={{
                        left: `${pctX(497.59)}%`,
                        top: `${pctY(12.43)}%`,
                    }}
                >
                    <SectionLabel>(About)</SectionLabel>
                </span>

                {/* Two-tone statement */}
                <p
                    className="absolute m-0 font-sans font-normal leading-[1.07] tracking-[-0.2vw]"
                    style={{
                        left: `${pctX(497.59)}%`,
                        top: `${pctY(0)}%`,
                        width: `${pctX(1405)}%`,
                        fontSize: '4.167vw',
                        textIndent: '15.36vw',
                    }}
                >
                    <span>{STATEMENT_LEAD}</span>
                    <span className="text-[#c4c4c4]">{STATEMENT_TAIL}</span>
                </p>

                {/* Get Started */}
                <button
                    type="button"
                    onClick={onGetStarted}
                    className="absolute flex items-center justify-center whitespace-nowrap rounded-full bg-[#333] font-sans font-normal tracking-[-0.03em] text-white shadow-[0px_3.243px_11.351px_rgba(0,0,0,0.11)]"
                    style={{
                        left: `${pctX(497.59)}%`,
                        top: `${pctY(459.67)}%`,
                        minWidth: `${pctX(230.27)}%`,
                        height: `${pctY(60)}%`,
                        paddingInline: '1.436vw',
                        fontSize: '1.182vw',
                    }}
                >
                    Get Started
                </button>
            </div>

            {/* Mobile — (About) nests into the statement's first-line indent,
                matching the Figma about frame (1113:31669). */}
            <div className="relative px-6 md:hidden">
                <span className="absolute left-6 top-[1.5vw]">
                    <SectionLabel>(About)</SectionLabel>
                </span>
                <p
                    className="m-0 font-sans text-[5.21vw] font-normal leading-[1.07] tracking-[-0.048em]"
                    style={{ textIndent: '19.06vw' }}
                >
                    <span>{STATEMENT_LEAD}</span>
                    <span className="text-[#c4c4c4]">{STATEMENT_TAIL}</span>
                </p>
            </div>
        </section>
    );
};
